import React, { useEffect, useRef, useState } from 'react';
import './AnimatedCounter.css';

const AnimatedCounter = ({ value, duration = 800, className = '', decimals = 0, prefix = '', suffix = '' }) => {
  // Ensure value is a valid number
  const numericValue = typeof value === 'number' && !isNaN(value) ? value : 0;
  
  const [displayValue, setDisplayValue] = useState(numericValue);
  const [isAnimating, setIsAnimating] = useState(false);
  const previousValue = useRef(numericValue);

  useEffect(() => {
    // Ensure value is a valid number
    const validValue = typeof value === 'number' && !isNaN(value) ? value : 0;
    
    // Only animate if value actually changed
    if (previousValue.current !== validValue) {
      setIsAnimating(true);
      
      const startValue = previousValue.current;
      const endValue = validValue;
      const startTime = Date.now();
      
      const animate = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function (easeOutExpo for smooth deceleration)
        const easeOutExpo = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
        
        const currentValue = startValue + (endValue - startValue) * easeOutExpo;
        setDisplayValue(currentValue);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setDisplayValue(endValue);
          setIsAnimating(false);
          previousValue.current = endValue;
        }
      };
      
      requestAnimationFrame(animate);
    }
  }, [value, duration]);

  // Format the number based on decimals
  const formattedValue = decimals > 0 
    ? displayValue.toFixed(decimals)
    : Math.round(displayValue).toString();

  // Split into individual digits for animation
  const digits = formattedValue.split('');

  return (
    <span className={`animated-counter ${isAnimating ? 'animating' : ''} ${className}`}>
      {prefix && <span className="counter-prefix">{prefix}</span>}
      {digits.map((char, index) => (
        <span
          key={`${index}-${char}`}
          className={`counter-digit ${char === '.' || char === ',' ? 'counter-separator' : ''}`}
          style={{
            animationDelay: `${index * 30}ms`
          }}
        >
          {char}
        </span>
      ))}
      {suffix && <span className="counter-suffix">{suffix}</span>}
    </span>
  );
};

export default AnimatedCounter;
